import React, { useEffect, useRef, useState } from "react";
import { AWS_BASE_URL } from "../../../../../clientConfig";
import TabsBox, { TabBody } from "../../../../../components/Tabs";
import SvgIcon from "../../../../../assets/icons/SvgIcon";
import { formatPrice } from "../../../../../utils";
import BtnContactAgent from "../../../../../components/Section/ContactAnAgent";
import SplideSlider from "../../../../../components/Slider/SplideSlider";
import FetchImages from "../../../../../constants/imagePath";
import SizeConverter from "../../../../../components/SizeConverter";

interface FloorPlanSectionProps {
  projectData: any;
  floorList: any[];
  imageClick: (clickIndex: number) => void;
}

const FloorPlanSection: React.FC<FloorPlanSectionProps> = ({
  projectData,
  imageClick,
  floorList,
}) => {
  const [currentBuildingList, setCurrentBuildingList] = useState<any>(
    floorList?.[0]?.list
  );
  const [activeBuildingType, setActiveBuildingType] = useState(
    floorList?.[0]?.building
  );
  const defaultActiveTab = useRef<string>(floorList?.[0]?.list?.[0]?._id);

  useEffect(() => {
    if (floorList?.[0]?.list?.[0]?._id) {
      setActiveBuildingType(floorList[0].building);
      defaultActiveTab.current = floorList?.[0]?.list?.[0]?._id;
      setCurrentBuildingList(floorList[0].list);
    }
  }, [floorList]);

  return (
    <section id="floor-plan" className="space-y-8 md:p-4">
      <div className="flex-1">
        <h2 className="font-normal text-dark-cyan text-3xl text-center">
          <span className="font-black">Floor Plan</span> & Pricing
        </h2>
      </div>
      <div className="px-4 py-1 project-gray-section">
        <div className="flex justify-start items-center gap-4 overflow-x-auto">
          {floorList?.map((floorItems: any, index: number) => (
            <div key={index} className="xs:py-3">
              <button
                className={`${activeBuildingType === floorItems?.building ? "bg-linear-gradient text-white" : ""} py-3 px-8 shadow rounded-md w-max border`}
                onClick={() => {
                  setActiveBuildingType(floorItems?.building);
                  defaultActiveTab.current = floorItems?.list?.[0]?._id;
                  setCurrentBuildingList(floorItems?.list);
                }}
              >
                {floorItems?.building}
              </button>
            </div>
          ))}
        </div>

        <TabsBox defaultEventKey={defaultActiveTab.current}>
          {currentBuildingList
            ?.sort((a: any, b: any) => {
              if (a?.building_type?.toString() === "0") {
                return a.building_interior_area - b.building_interior_area;
              } else {
                return a.building_outer_area - b.building_outer_area;
              }
            })
            ?.map((item: any, index: number) => {
              const buildingArea =
                item?.building_outer_area !== 0
                  ? item?.building_outer_area
                  : item?.building_interior_area;

              const buildingWing = item?.building_wing?.length
                ? `(${item?.building_wing})`
                : "";

              const tabTitle = `${buildingWing}${buildingArea} sq-ft SBA`;
              return (
                <TabBody
                  key={`${item?._id}-${index}`}
                  eventKey={item?._id}
                  title={tabTitle}
                >
                  <div className="gap-6 grid grid-cols-12 py-4 w-full">
                    <div className="col-span-12 2xl:col-span-8 xl:col-span-6">
                      <SplideSlider
                        showItems={1}
                        options={{
                          perPage: 1,
                          pagination: false,
                          breakpoints: {
                            320: {
                              perPage: 1,
                              perMove: 1,
                            },
                          },
                        }}
                      >
                        {item?.building_floor_plan?.map(
                          (floor_plan: any, floorPlanIndex: number) => (
                            <button
                              key={`${floorPlanIndex}`}
                              className="w-full"
                              onClick={() => {
                                const indexOfFloorPlan = floorList
                                  ?.sort(
                                    (a: any, b: any) =>
                                      a?.building?.split(" ")?.[0] -
                                      b?.building?.split(" ")?.[0]
                                  )
                                  ?.map((item) => item?.list)
                                  ?.flat(1)
                                  ?.map((item) => item?.building_floor_plan)
                                  ?.flat(1)
                                  ?.indexOf(floor_plan);
                                imageClick(indexOfFloorPlan);
                              }}
                            >
                              <div className="relative w-full md:h-[475px]">
                                <img
                                  src={
                                    floor_plan
                                      ? `${AWS_BASE_URL}${floor_plan?.image}`
                                      : ""
                                  }
                                  alt={projectData?.name}
                                  className="w-full h-full md:h-[475px] object-contain"
                                  onError={(e) =>
                                    ((e.target as HTMLImageElement).src =
                                      FetchImages.ImageNotFound)
                                  }
                                />
                                {floor_plan?.label ? (
                                  <div className="top-4 right-4 absolute bg-gray-300 shadow px-4 py-1 rounded text-black">
                                    <span className="font-medium text-primary text-base">
                                      {floor_plan?.label}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            </button>
                          )
                        )}
                      </SplideSlider>
                    </div>
                    <div className="col-span-12 2xl:col-span-4 xl:col-span-6 bg-white shadow px-4 xs:px-6 py-4 border rounded-md text-base xs:text-lg">
                      <div className="space-y-4">
                        {item?.building_expected_price ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6 font-normal text-[#707070]">
                              ₹
                            </div>
                            <h3 className="font-normal text-[#707070]">
                              {formatPrice(item?.building_expected_price)}
                            </h3>
                          </div>
                        ) : (
                          <BtnContactAgent
                            className={`h-fit w-max lg:w-max pb-1 pt-0 px-3 font-medium`}
                            initialData={{
                              project_id: projectData?._id,
                            }}
                          >
                            Price on Request
                          </BtnContactAgent>
                        )}
                        {item?.building_units ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="each-floor-building"
                                className="size-4"
                              />
                            </div>
                            <h3 className="font-normal text-[#707070]">
                              {item?.building_units
                                ? `${item?.building_units} units`
                                : ""}
                            </h3>
                          </div>
                        ) : null}

                        {item?.building_interior_area ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="circle-sq-ft-icon"
                                className="size-4"
                              />
                            </div>
                            <SizeConverter
                              baseSize={item?.building_interior_area}
                              className="font-normal text-[#707070]"
                            />
                          </div>
                        ) : null}

                        {item?.unit_on_each_floor ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="each-floor-building"
                                className="size-4"
                              />
                            </div>
                            <h3 className="font-normal text-[#707070]">
                              Unit on each floor: {item?.unit_on_each_floor}
                            </h3>
                          </div>
                        ) : null}

                        {item?.no_of_lifts ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="lift-icon"
                                className="size-4"
                              />
                            </div>
                            <h3 className="font-normal text-[#707070]">
                              Lifts: {item?.no_of_lifts}
                            </h3>
                          </div>
                        ) : null}

                        {item?.no_of_parking?.four_wheeler ||
                        item?.no_of_parking?.two_wheeler ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="parking-icon"
                                className="size-4"
                              />
                            </div>
                            <div className="flex gap-2">
                              <h3 className="font-normal text-[#707070]">
                                Parking:
                              </h3>
                              {item?.no_of_parking?.four_wheeler ? (
                                <div className="flex justify-start items-center gap-2 font-normal text-[#707070]">
                                  <span>
                                    {item?.no_of_parking?.four_wheeler}
                                  </span>
                                  <SvgIcon
                                    iconName="car-icon"
                                    className="size-4"
                                  />
                                </div>
                              ) : null}
                              {item?.no_of_parking?.two_wheeler ? (
                                <div className="flex justify-start items-center gap-2 font-normal text-[#707070]">
                                  <span>
                                    {item?.no_of_parking?.two_wheeler}
                                  </span>
                                  <SvgIcon
                                    iconName="motorcycle-icon"
                                    className="size-4"
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                        {item?.no_of_balcony ? (
                          <div className="flex justify-start items-center gap-2">
                            <div className="w-6">
                              <SvgIcon
                                iconName="balconies-icon"
                                className="size-4"
                              />
                            </div>
                            <h3 className="font-normal text-[#707070]">
                              {item?.no_of_balcony} Balconies
                            </h3>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </TabBody>
              );
            })}
        </TabsBox>
      </div>
    </section>
  );
};

export default FloorPlanSection;
